// ============================================================
// leaderboard.ts — Leaderboard table with search, pagination
// DOM-based component, dark military theme.
// ============================================================

import { createRankBadge, getRankTier, type RankTier } from './rank-badge';

const ACCENT = '#e94560';
const BG_DARK = '#1a1a2e';
const BG_PANEL = '#16213e';
const GOLD = '#FFD700';
const SILVER = '#C0C0C0';
const BRONZE = '#CD7F32';

// ── Types ───────────────────────────────────────────────────

export interface LeaderboardEntry {
  rank: number;
  name: string;
  rating: number;
  tier: RankTier;
  wins: number;
  losses: number;
  winRate: number; // 0-100
}

export interface MyRankInfo {
  rank: number;
  name: string;
  rating: number;
  tier: RankTier;
  wins: number;
  losses: number;
  winRate: number;
}

// ── State ───────────────────────────────────────────────────

let overlay: HTMLElement | null = null;
let allEntries: LeaderboardEntry[] = [];
let filteredEntries: LeaderboardEntry[] = [];
let myRank: MyRankInfo | null = null;
let currentPage = 0;
const PAGE_SIZE = 20;

// ── Style injection ─────────────────────────────────────────

let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
    #leaderboard-overlay {
      position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
      background: rgba(0,0,0,0.92);
      z-index: 140;
      display: none;
      font-family: 'Courier New', monospace;
      color: #ffffff;
      user-select: none;
      overflow-y: auto;
    }

    .lb-container {
      max-width: 800px;
      margin: 0 auto;
      padding: 40px 20px;
    }

    .lb-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }

    .lb-title {
      font-size: 28px; font-weight: bold;
      color: ${ACCENT}; letter-spacing: 4px;
      text-transform: uppercase;
      text-shadow: 0 0 20px rgba(233,69,96,0.3);
    }

    .lb-close-btn {
      font-size: 24px; color: #666;
      cursor: pointer; padding: 4px 10px;
      transition: color 0.15s;
    }
    .lb-close-btn:hover { color: #fff; }

    .lb-search {
      width: 100%; padding: 10px 14px;
      font-size: 13px; font-family: 'Courier New', monospace;
      background: rgba(30, 34, 40, 0.85);
      color: #ccc; border: 1px solid #333;
      letter-spacing: 1px; outline: none;
      margin-bottom: 16px;
      transition: border-color 0.15s;
    }
    .lb-search::placeholder { color: #444; }
    .lb-search:focus { border-color: #555; }

    .lb-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
      margin-bottom: 16px;
    }

    .lb-table th {
      text-align: left;
      padding: 8px 6px;
      color: #666;
      font-weight: bold;
      letter-spacing: 1px;
      text-transform: uppercase;
      font-size: 10px;
      border-bottom: 2px solid ${ACCENT};
    }
    .lb-table th.center { text-align: center; }

    .lb-table td {
      padding: 8px 6px;
      border-bottom: 1px solid #1a1a2a;
      color: #bbb;
    }
    .lb-table td.center { text-align: center; }

    .lb-table tr:hover td {
      background: rgba(233,69,96,0.06);
    }

    .lb-rank-cell {
      font-weight: bold;
      font-size: 14px;
      width: 50px;
      text-align: center;
    }

    .lb-name-cell {
      font-weight: bold;
      letter-spacing: 1px;
    }

    .lb-badge-cell .rank-badge {
      transform: scale(0.75);
      transform-origin: left center;
    }

    .lb-pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
    }

    .lb-page-btn {
      padding: 6px 16px; font-size: 12px;
      font-family: 'Courier New', monospace;
      color: #888; background: transparent;
      border: 1px solid #333; cursor: pointer;
      letter-spacing: 1px; transition: all 0.15s;
    }
    .lb-page-btn:hover:not(:disabled) {
      color: #fff; border-color: #666;
    }
    .lb-page-btn:disabled {
      opacity: 0.3; cursor: not-allowed;
    }

    .lb-page-info {
      font-size: 12px; color: #555;
      letter-spacing: 1px;
    }

    .lb-my-rank {
      background: linear-gradient(135deg, ${BG_PANEL}, ${BG_DARK});
      border: 1px solid ${ACCENT};
      border-radius: 4px;
      padding: 14px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .lb-my-rank-label {
      font-size: 10px; color: #666;
      letter-spacing: 2px; text-transform: uppercase;
      margin-bottom: 4px;
    }

    .lb-my-rank-name {
      font-size: 16px; font-weight: bold;
      color: ${ACCENT}; letter-spacing: 1px;
    }

    .lb-my-rank-stats {
      display: flex; gap: 24px;
      align-items: center;
    }

    .lb-my-rank-stat {
      text-align: center;
    }
    .lb-my-rank-stat-label {
      font-size: 9px; color: #555;
      letter-spacing: 1px; text-transform: uppercase;
    }
    .lb-my-rank-stat-value {
      font-size: 16px; font-weight: bold; color: #ccc;
    }
  `;
  document.head.appendChild(style);
}

// ── Helpers ─────────────────────────────────────────────────

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function getRankColor(rank: number): string | null {
  if (rank === 1) return GOLD;
  if (rank === 2) return SILVER;
  if (rank === 3) return BRONZE;
  return null;
}

function ensureOverlay(): HTMLElement {
  if (overlay) return overlay;
  injectStyles();
  overlay = document.createElement('div');
  overlay.id = 'leaderboard-overlay';
  document.body.appendChild(overlay);
  return overlay;
}

function totalPages(): number {
  return Math.max(1, Math.ceil(filteredEntries.length / PAGE_SIZE));
}

function render(): void {
  const el = ensureOverlay();
  el.innerHTML = '';

  const container = document.createElement('div');
  container.className = 'lb-container';

  // Header
  const header = document.createElement('div');
  header.className = 'lb-header';

  const title = document.createElement('div');
  title.className = 'lb-title';
  title.textContent = 'LEADERBOARD';
  header.appendChild(title);

  const closeBtn = document.createElement('div');
  closeBtn.className = 'lb-close-btn';
  closeBtn.textContent = '\u2715';
  closeBtn.addEventListener('click', () => hideLeaderboard());
  header.appendChild(closeBtn);

  container.appendChild(header);

  // Search
  const search = document.createElement('input');
  search.className = 'lb-search';
  search.type = 'text';
  search.placeholder = 'Search player...';
  search.addEventListener('input', () => {
    const query = search.value.trim().toLowerCase();
    if (query.length === 0) {
      filteredEntries = allEntries;
    } else {
      filteredEntries = allEntries.filter((e) => e.name.toLowerCase().includes(query));
    }
    currentPage = 0;
    renderTable(container);
  });
  container.appendChild(search);

  // Table + pagination placeholder
  renderTable(container);

  // My rank section
  if (myRank) {
    const mySection = document.createElement('div');
    mySection.className = 'lb-my-rank';

    const leftCol = document.createElement('div');
    const label = document.createElement('div');
    label.className = 'lb-my-rank-label';
    label.textContent = 'YOUR RANK';
    leftCol.appendChild(label);

    const nameRow = document.createElement('div');
    nameRow.style.display = 'flex';
    nameRow.style.alignItems = 'center';
    nameRow.style.gap = '12px';

    const rankNum = document.createElement('span');
    rankNum.style.cssText = 'font-size:20px;font-weight:bold;color:#888;';
    rankNum.textContent = `#${myRank.rank}`;
    nameRow.appendChild(rankNum);

    const nameEl = document.createElement('span');
    nameEl.className = 'lb-my-rank-name';
    nameEl.textContent = escapeHtml(myRank.name);
    nameRow.appendChild(nameEl);

    leftCol.appendChild(nameRow);
    mySection.appendChild(leftCol);

    const stats = document.createElement('div');
    stats.className = 'lb-my-rank-stats';

    // Badge
    const badge = createRankBadge(myRank.rating, myRank.tier);
    stats.appendChild(badge);

    // W/L
    const wlStat = document.createElement('div');
    wlStat.className = 'lb-my-rank-stat';
    wlStat.innerHTML =
      `<div class="lb-my-rank-stat-label">W/L</div>` +
      `<div class="lb-my-rank-stat-value">${myRank.wins}/${myRank.losses}</div>`;
    stats.appendChild(wlStat);

    // Win Rate
    const wrStat = document.createElement('div');
    wrStat.className = 'lb-my-rank-stat';
    wrStat.innerHTML =
      `<div class="lb-my-rank-stat-label">Win Rate</div>` +
      `<div class="lb-my-rank-stat-value">${myRank.winRate.toFixed(1)}%</div>`;
    stats.appendChild(wrStat);

    mySection.appendChild(stats);
    container.appendChild(mySection);
  }

  el.appendChild(container);
}

function renderTable(container: HTMLElement): void {
  // Remove old table + pagination if re-rendering
  const oldTable = container.querySelector('.lb-table-wrap');
  if (oldTable) oldTable.remove();
  const oldPag = container.querySelector('.lb-pagination');
  if (oldPag) oldPag.remove();

  const tableWrap = document.createElement('div');
  tableWrap.className = 'lb-table-wrap';
  tableWrap.setAttribute('role', 'table');
  tableWrap.setAttribute('aria-label', 'Leaderboard standings');

  const table = document.createElement('table');
  table.className = 'lb-table';

  // Thead
  const thead = document.createElement('thead');
  const hRow = document.createElement('tr');
  hRow.setAttribute('role', 'row');
  const cols = [
    { label: '#', cls: 'center' },
    { label: 'Player', cls: '' },
    { label: 'Rating', cls: 'center' },
    { label: 'Tier', cls: '' },
    { label: 'W/L', cls: 'center' },
    { label: 'Win %', cls: 'center' },
  ];
  for (const col of cols) {
    const th = document.createElement('th');
    if (col.cls) th.className = col.cls;
    th.textContent = col.label;
    hRow.appendChild(th);
  }
  thead.appendChild(hRow);
  table.appendChild(thead);

  // Tbody
  const tbody = document.createElement('tbody');
  const start = currentPage * PAGE_SIZE;
  const pageEntries = filteredEntries.slice(start, start + PAGE_SIZE);

  for (const entry of pageEntries) {
    const row = document.createElement('tr');
    row.setAttribute('role', 'row');
    const rankColor = getRankColor(entry.rank);

    // Rank #
    const rankTd = document.createElement('td');
    rankTd.className = 'lb-rank-cell';
    rankTd.textContent = String(entry.rank);
    if (rankColor) rankTd.style.color = rankColor;
    row.appendChild(rankTd);

    // Name
    const nameTd = document.createElement('td');
    nameTd.className = 'lb-name-cell';
    nameTd.textContent = escapeHtml(entry.name);
    if (rankColor) nameTd.style.color = rankColor;
    row.appendChild(nameTd);

    // Rating
    const ratingTd = document.createElement('td');
    ratingTd.className = 'center';
    ratingTd.style.fontWeight = 'bold';
    ratingTd.textContent = String(entry.rating);
    row.appendChild(ratingTd);

    // Tier badge (compact)
    const tierTd = document.createElement('td');
    tierTd.className = 'lb-badge-cell';
    const badge = createRankBadge(entry.rating, entry.tier);
    tierTd.appendChild(badge);
    row.appendChild(tierTd);

    // W/L
    const wlTd = document.createElement('td');
    wlTd.className = 'center';
    wlTd.textContent = `${entry.wins}/${entry.losses}`;
    row.appendChild(wlTd);

    // Win %
    const wrTd = document.createElement('td');
    wrTd.className = 'center';
    wrTd.textContent = `${entry.winRate.toFixed(1)}%`;
    row.appendChild(wrTd);

    tbody.appendChild(row);
  }

  if (pageEntries.length === 0) {
    const emptyRow = document.createElement('tr');
    emptyRow.setAttribute('role', 'row');
    const emptyTd = document.createElement('td');
    emptyTd.colSpan = 6;
    emptyTd.style.cssText = 'text-align:center;padding:30px;color:#444;letter-spacing:2px;';
    emptyTd.textContent = 'NO RESULTS';
    emptyRow.appendChild(emptyTd);
    tbody.appendChild(emptyRow);
  }

  table.appendChild(tbody);
  tableWrap.appendChild(table);

  // Insert before my-rank section
  const myRankEl = container.querySelector('.lb-my-rank');
  if (myRankEl) {
    container.insertBefore(tableWrap, myRankEl);
  } else {
    container.appendChild(tableWrap);
  }

  // Pagination
  const tp = totalPages();
  if (tp > 1) {
    const pagination = document.createElement('div');
    pagination.className = 'lb-pagination';

    const prevBtn = document.createElement('button');
    prevBtn.className = 'lb-page-btn';
    prevBtn.textContent = '\u25C0 PREV';
    prevBtn.disabled = currentPage === 0;
    prevBtn.addEventListener('click', () => {
      if (currentPage > 0) {
        currentPage--;
        renderTable(container);
      }
    });
    pagination.appendChild(prevBtn);

    const pageInfo = document.createElement('span');
    pageInfo.className = 'lb-page-info';
    pageInfo.textContent = `${currentPage + 1} / ${tp}`;
    pagination.appendChild(pageInfo);

    const nextBtn = document.createElement('button');
    nextBtn.className = 'lb-page-btn';
    nextBtn.textContent = 'NEXT \u25B6';
    nextBtn.disabled = currentPage >= tp - 1;
    nextBtn.addEventListener('click', () => {
      if (currentPage < tp - 1) {
        currentPage++;
        renderTable(container);
      }
    });
    pagination.appendChild(nextBtn);

    if (myRankEl) {
      container.insertBefore(pagination, myRankEl);
    } else {
      container.appendChild(pagination);
    }
  }
}

// ── Public API ──────────────────────────────────────────────

/** Show the leaderboard with entries and local player rank info. */
export function showLeaderboard(entries: LeaderboardEntry[], myRankInfo?: MyRankInfo): void {
  allEntries = entries;
  filteredEntries = entries;
  myRank = myRankInfo ?? null;
  currentPage = 0;

  render();
  const el = ensureOverlay();
  el.style.display = 'block';
}

/** Hide the leaderboard overlay. */
export function hideLeaderboard(): void {
  if (overlay) {
    overlay.style.display = 'none';
    overlay.innerHTML = '';
  }
}
